import React, { useCallback } from 'react';
import { Button, ButtonProps } from './button';
import { cn } from '@/lib/utils';

type HapticType = 'light' | 'medium' | 'heavy' | 'selection' | 'impact' | 'notification';

interface HapticButtonProps extends ButtonProps {
  hapticType?: HapticType;
  hapticEnabled?: boolean;
  pressAnimation?: boolean;
}

// Haptic feedback utility
const triggerHaptic = (type: HapticType) => {
  // Check if device supports haptic feedback
  if ('vibrate' in navigator) {
    const patterns = {
      light: [10],
      medium: [20],
      heavy: [30],
      selection: [5],
      impact: [15],
      notification: [10, 10, 10],
    };
    
    navigator.vibrate(patterns[type]);
  }
  
  // For devices that support the Haptic API (iOS Safari)
  if ('haptic' in navigator) {
    try {
      (navigator as unknown as { haptic: { impact: (t: string) => void } }).haptic.impact(type);
    } catch (error) {
      // Fallback to vibration
      console.debug('Haptic API not supported, falling back to vibration');
    }
  }
};

export const HapticButton: React.FC<HapticButtonProps> = ({
  children,
  hapticType = 'light',
  hapticEnabled = true,
  pressAnimation = true,
  onClick,
  className,
  ...props
}) => {
  const handleClick = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    if (hapticEnabled) {
      triggerHaptic(hapticType);
    }
    
    onClick?.(event);
  }, [hapticEnabled, hapticType, onClick]);

  return (
    <Button
      onClick={handleClick}
      className={cn(
        "touch-manipulation transition-all duration-150",
        pressAnimation && "active:scale-95 active:brightness-95",
        className
      )}
      {...props}
    >
      {children}
    </Button>
  );
};